import { useState, useEffect } from "react";
import { API_BASE_URL, getCachedPartners, setCachedPartners, mockPartners } from "@/mocks/adminData";

interface Partner {
  id: number;
  name: string;
  handle: string;
  tg_url: string;
  avatar_url: string;
  dept: string;
  sort: number;
  status: number;
}

const DEPT_LABELS: Record<string, string> = {
  promotion: "推广招商部",
  third: "三方招商部",
  investment: "招商部",
};

function getDeptLabel(dept: string): string {
  return DEPT_LABELS[dept] || dept;
}

function PartnerCard({ partner }: { partner: Partner }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(partner.handle).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-3 min-w-0">
        <a href={partner.tg_url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
          {partner.avatar_url ? (
            <img src={partner.avatar_url} alt={partner.name} className="w-11 h-11 rounded-full object-cover border border-gray-100" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white font-bold text-sm">
              {partner.name.charAt(0)}
            </div>
          )}
        </a>
        <span className="text-sm font-semibold text-gray-800 whitespace-nowrap w-10 flex-shrink-0">{partner.name}</span>
        <span className="text-gray-300 text-sm flex-shrink-0">丨</span>
        <a href={partner.tg_url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 min-w-0 cursor-pointer">
          <i className="ri-telegram-fill text-sky-500 text-lg flex-shrink-0"></i>
          <span className="text-sm text-sky-500 font-medium truncate">{partner.handle}</span>
        </a>
      </div>
      <button onClick={handleCopy} title="复制"
        className="w-8 h-8 flex-shrink-0 flex items-center justify-center border border-gray-200 rounded-lg ml-2 cursor-pointer hover:bg-gray-50 transition-colors">
        {copied
          ? <i className="ri-check-line text-green-500 text-sm"></i>
          : <i className="ri-file-copy-line text-gray-400 text-sm"></i>}
      </button>
    </div>
  );
}

export default function BusinessSection() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    // 优先级：本地缓存 > mock 数据，先立即显示
    const cached = getCachedPartners();
    const initialData = cached ?? mockPartners;
    const enabled0 = initialData.filter(p => p.status === undefined || p.status === 1);
    const display0 = enabled0.length > 0 ? enabled0 : initialData;
    setPartners(display0);
    setActiveTab(display0[0]?.dept ?? "");

    // 再尝试从后端拉取最新数据
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    fetch(`${API_BASE_URL}/api/partners/list`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal,
    })
      .then(res => {
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        try {
          let list: Partner[] = [];
          if (data && data.code === 200 && data.data) {
            if (Array.isArray(data.data)) {
              list = data.data;
            } else if (data.data && Array.isArray(data.data.list)) {
              list = data.data.list;
            }
          }
          if (Array.isArray(list) && list.length > 0) {
            const enabled = list.filter(p => p.status === undefined || p.status === 1);
            if (enabled.length > 0) {
              setPartners(enabled);
              setActiveTab(enabled[0].dept);
              setCachedPartners(list); // 更新缓存
            }
          }
        } catch {
          // 解析失败保持当前数据
        }
      })
      .catch(() => {
        clearTimeout(timeoutId);
        // 网络失败保持当前显示数据不变
      });
  }, []);

  const depts = Array.from(new Set(partners.map(p => p.dept)));
  const currentList = partners.filter(p => p.dept === activeTab);

  if (partners.length === 0) return null;

  return (
    <section id="business" className="w-full py-5 md:py-8" style={{ background: "#eef2f7" }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-10">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="w-1 h-5 rounded-full bg-red-500 flex-shrink-0"></span>
          <h2 className="text-base md:text-lg font-bold text-gray-800 whitespace-nowrap">官方商务</h2>
          <span className="text-gray-300 text-xs flex-shrink-0">丨</span>
          <span className="text-xs text-gray-400">只认准以下商务人员，其他勿信，谨防被骗</span>
        </div>

        {/* PC端：多列展示 */}
        <div className="hidden md:block">
          {/* Tab 切换 */}
          <div className="flex border-b border-gray-100 mb-4">
            {depts.map(dept => (
              <button
                key={dept}
                onClick={() => setActiveTab(dept)}
                className={`px-6 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === dept
                    ? "text-gray-800 border-b-2 border-gray-800"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {getDeptLabel(dept)}
              </button>
            ))}
          </div>
          {/* 多列网格 */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8">
            {currentList.map(p => (
              <PartnerCard key={p.id} partner={p} />
            ))}
            {currentList.length === 0 && (
              <div className="col-span-3 text-center py-6 text-gray-400 text-sm">暂无人员</div>
            )}
          </div>
        </div>

        {/* 手机端：单列 */}
        <div className="md:hidden">
          <div className="flex border-b border-gray-100 mb-3">
            {depts.map(dept => (
              <button
                key={dept}
                onClick={() => setActiveTab(dept)}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === dept
                    ? "text-gray-800 border-b-2 border-gray-800"
                    : "text-gray-400"
                }`}
              >
                {getDeptLabel(dept)}
              </button>
            ))}
          </div>
          <div>
            {currentList.map(p => (
              <PartnerCard key={p.id} partner={p} />
            ))}
            {currentList.length === 0 && (
              <div className="text-center py-6 text-gray-400 text-sm">暂无人员</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
